import React, { useContext, useEffect, useState } from 'react';
import { FormGroup, Modal, ModalBody, Spinner } from 'reactstrap';
import PayeeDetails from './PayeeDetails';
import { fetchVendorDetail } from '~/services/fundrequests';
import { VendorOption } from '~/types/fundrequests';
import { useFormContext } from 'react-hook-form';
import { SelectInput } from '~/shared/components/form/Inputs';
import AchInfoForm from './AchInfoForm';
import { FundRequestContext } from '~/context/fundrequests';
import { CampaignContext } from '~/context/campaign';

type Props = {
    showOtherPayee: boolean;
    toggleSwapPayee: boolean;
    setToggleSwapPayee: React.Dispatch<React.SetStateAction<boolean>>;
    setPayeesFound: React.Dispatch<React.SetStateAction<boolean>>;
    payeesFound: boolean;
    initialLoadComplete?: boolean;
};

interface CustomOptionProps {
    label: string;
    verified?: boolean;
}

function PayeeSelect({
    showOtherPayee = false,
    setToggleSwapPayee,
    toggleSwapPayee,
    setPayeesFound,
    payeesFound,
    initialLoadComplete = true,
}: Props): JSX.Element {
    const { watch, control, setValue } = useFormContext();
    const [{ fundRequest, vendors, selectedPayee }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [payees, setPayees] = useState<VendorOption[]>([]);
    const [loadingPayee, setLoadingPayee] = useState(false);
    const currentVendorId = watch('vendor');
    const paymentType = watch('payment_type');

    const handleSwapPayee = () => {
        setToggleSwapPayee(true);
        setValue('vendor', '');
        fundRequestDispatch({ type: 'set_selected_payee', payload: null });
    };

    // Handle form value changes
    useEffect(() => {
        if (currentVendorId && payees && payees.length > 0) {
            const payee = payees.find((p) => p.id === currentVendorId);
            if (payee) {
                setToggleSwapPayee(false);
                // Only fetch vendor detail if it's not the currently selected payee
                // and only if we're not during the initial load (which would cause the stagger)
                if (!selectedPayee || selectedPayee.id !== currentVendorId) {
                    const campaignId = campaign?.campaignDetail?.campaign_id;
                    getVendorDetail(Number(currentVendorId), campaignId);
                }
            }
        } else if (!currentVendorId || currentVendorId === '') {
            // No vendor selected - ensure we show the vendor selection UI
            setToggleSwapPayee(true);
            fundRequestDispatch({ type: 'set_selected_payee', payload: null });
        }
    }, [currentVendorId, payees]);

    // Handle context selectedPayee changes
    useEffect(() => {
        if (selectedPayee && selectedPayee.id) {
            // Update form value if it doesn't match the selected payee
            if (currentVendorId !== selectedPayee.id) {
                setValue('vendor', selectedPayee.id);
            }
            setToggleSwapPayee(false);

            // Set the primary address if available
            if (selectedPayee.vendor_addresses?.length > 0) {
                const primaryAddress = selectedPayee.vendor_addresses.find(
                    (address: any) => address.is_primary === true
                );

                if (primaryAddress) {
                    setValue('vendor_address', primaryAddress.id);
                } else {
                    setValue(
                        'vendor_address',
                        selectedPayee.vendor_addresses[0].id
                    );
                }
            }
        }
    }, [selectedPayee]);

    const getVendorDetail = async (vendorId: number, campaignId?: number) => {
        if (!campaignId) {
            return;
        }

        setLoadingPayee(true);

        try {
            const resp = await fetchVendorDetail(vendorId, campaignId);
            fundRequestDispatch({ type: 'set_selected_payee', payload: resp });
        } catch (error) {
            console.log('Error fetching vendor detail:', error);
        } finally {
            setLoadingPayee(false);
        }
    };

    useEffect(() => {
        if (vendors) {
            // Sort vendors alphabetically by name
            const sortedVendors = [...vendors].sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setPayees(sortedVendors);
            setPayeesFound(true);
        } else {
            setToggleSwapPayee(true);
            setPayeesFound(false);
        }
    }, [vendors]);

    const CustomOption = (props: CustomOptionProps) => {
        return `<div class='d-flex justify-content-between'>
                ${props.label} ${
            props.verified
                ? `<span class='text-primary fw-bold'>verified</span>`
                : ''
        }
            </div>`;
    };

    const fundRequestStatus = fundRequest?.fund_request_status?.value;

    const showAchInfoForm =
        fundRequest?.needs_ach_info &&
        !toggleSwapPayee &&
        fundRequestStatus == 'needs_info' &&
        paymentType === 'ACH';

    return (
        <>
            <Modal
                isOpen={Boolean(loadingPayee && initialLoadComplete)}
                centered
                backdrop='static'
                keyboard={false}
            >
                <ModalBody className='text-center py-5'>
                    <Spinner color='primary' />
                    <div className='mt-3 fw-bold'>
                        Loading payee information...
                    </div>
                </ModalBody>
            </Modal>
            {payeesFound && toggleSwapPayee && (
                <FormGroup>
                    <SelectInput
                        name='vendor'
                        label='Select Payee'
                        required={!showOtherPayee}
                        options={payees.map((payee) => ({
                            value: payee.id,
                            label: payee.name,
                            verified: payee.verified_in_mip,
                        }))}
                        formatOptionLabel={function (data) {
                            return (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: CustomOption(data),
                                    }}
                                />
                            );
                        }}
                        control={control}
                    />
                </FormGroup>
            )}

            {!toggleSwapPayee && (
                <PayeeDetails
                    loadingPayee={loadingPayee}
                    handleSwapPayee={handleSwapPayee}
                    initialLoadComplete={initialLoadComplete}
                />
            )}
            {showAchInfoForm && <AchInfoForm />}
        </>
    );
}

export default PayeeSelect;
